<script lang="ts">
  import { Pacientes, Bitacora, type Paciente } from '../lib/api';
  import { edadDesdeNacimiento } from '../lib/csv-import';

  type VistaPacientes = 'activos' | 'archivo';

  let items = $state<Paciente[]>([]);
  let q = $state('');
  let vista = $state<VistaPacientes>('activos');
  let filtroMes = $state(new Date().toISOString().slice(0, 7));
  let loading = $state(false);
  let error = $state<string | null>(null);

  let nuevo = $state<Paciente>({ nombres: '', apellidos: '', sexo: 'M', esNn: false, activo: true });
  let editandoId = $state<string | null>(null);
  let creando = $state(false);

  async function cargar() {
    loading = true; error = null;
    try {
      items = await Pacientes.list(q || undefined, {
        activo: vista === 'activos',
        mes: filtroMes || undefined,
      });
    }
    catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  function cambiarVista(v: VistaPacientes) {
    vista = v;
    editandoId = null;
    void cargar();
  }

  function editar(p: Paciente) {
    editandoId = p.id ?? null;
    nuevo = {
      nombres: p.nombres, apellidos: p.apellidos, cui: p.cui ?? '', sexo: p.sexo ?? 'M',
      fechaNacimiento: p.fechaNacimiento ?? '', telefono: p.telefono ?? '', email: p.email ?? '',
      direccion: p.direccion ?? '', esNn: p.esNn ?? false, activo: p.activo ?? true,
    };
  }

  function cancelarEdicion() {
    editandoId = null;
    nuevo = { nombres: '', apellidos: '', sexo: 'M', esNn: false, activo: true };
  }

  async function eliminar(p: Paciente) {
    if (!confirm(`¿Desactivar a ${p.nombres} ${p.apellidos}? Pasará al archivo y no aparecerá en búsquedas activas.`)) return;
    try {
      await Pacientes.eliminar(p.id!);
      Bitacora.registrar({
        action: 'paciente.desactivar',
        resourceType: 'paciente',
        resourceId: p.id,
        details: { nombre: `${p.nombres} ${p.apellidos}` },
      }).catch(() => {});
      await cargar();
    }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function reactivar(p: Paciente) {
    if (!confirm(`¿Reactivar a ${p.nombres} ${p.apellidos}?`)) return;
    try {
      await Pacientes.actualizar(p.id!, { ...p, activo: true });
      Bitacora.registrar({
        action: 'paciente.reactivar',
        resourceType: 'paciente',
        resourceId: p.id,
        details: { nombre: `${p.nombres} ${p.apellidos}` },
      }).catch(() => {});
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function guardar(e: SubmitEvent) {
    e.preventDefault();
    creando = true; error = null;
    try {
      const body: Paciente = { ...nuevo };
      if (!body.cui) body.cui = null;
      if (!body.fechaNacimiento) body.fechaNacimiento = null;
      if (editandoId) {
        await Pacientes.actualizar(editandoId, body);
      } else {
        await Pacientes.crear(body);
      }
      nuevo = { nombres: '', apellidos: '', sexo: 'M', esNn: false, activo: true };
      editandoId = null;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { creando = false; }
  }

  function fechaLista(p: Paciente): string {
    const raw = vista === 'archivo' ? p.updatedAt : p.createdAt;
    return raw ? new Date(raw).toLocaleDateString('es-GT') : '—';
  }

  $effect(() => { cargar(); });
</script>

<section class="view">
  <header class="view-header">
    <div>
      <h2>Pacientes</h2>
      <p class="muted">Registro, archivo de inactivos y búsqueda por nombre o CUI</p>
    </div>
    <div class="toolbar">
      <div class="tabs">
        <button type="button" class:active={vista === 'activos'} onclick={() => cambiarVista('activos')}>Activos</button>
        <button type="button" class:active={vista === 'archivo'} onclick={() => cambiarVista('archivo')}>Archivo</button>
      </div>
      <label class="field mes"><span>Mes</span>
        <input type="month" bind:value={filtroMes} onchange={cargar} />
      </label>
      <div class="search">
        <input placeholder="Buscar nombre, apellido o CUI…" bind:value={q} oninput={cargar} />
      </div>
    </div>
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}

  {#if vista === 'activos'}
    <form onsubmit={guardar} class="panel">
      <h3 class="sub">{editandoId ? 'Editar paciente' : 'Registrar paciente'}</h3>
      <div class="form-grid">
        <label class="field"><span>Nombres *</span><input placeholder="Juan" bind:value={nuevo.nombres} required /></label>
        <label class="field"><span>Apellidos *</span><input placeholder="Pérez" bind:value={nuevo.apellidos} required /></label>
        <label class="field"><span>CUI (opcional)</span><input placeholder="13 dígitos" bind:value={nuevo.cui} maxlength="13" /></label>
        <label class="field"><span>F. nacimiento</span><input type="date" bind:value={nuevo.fechaNacimiento} /></label>
        <label class="field"><span>Sexo</span><select bind:value={nuevo.sexo}><option value="M">M</option><option value="F">F</option><option value="O">O</option></select></label>
        <label class="field"><span>Teléfono</span><input placeholder="+502" bind:value={nuevo.telefono} /></label>
        <label class="field"><span>Email</span><input type="email" placeholder="correo@…" bind:value={nuevo.email} /></label>
        <label class="field"><span>Dirección</span><input placeholder="Dirección" bind:value={nuevo.direccion} /></label>
        <label class="chk field"><input type="checkbox" bind:checked={nuevo.esNn} /> NN (no identificado)</label>
      </div>
      <button type="submit" class="btn" disabled={creando}>{creando ? 'Guardando…' : (editandoId ? 'Guardar cambios' : '+ Registrar paciente')}</button>
      {#if editandoId}<button type="button" class="btn ghost" onclick={cancelarEdicion}>Cancelar</button>{/if}
    </form>
  {:else}
    <p class="hint">Pacientes desactivados. El expediente se conserva; puede reactivarlos cuando vuelvan a atenderse.</p>
  {/if}

  <table class="tabla">
    <thead>
      <tr>
        <th>Nombre</th><th>CUI</th><th>F. Nac.</th><th>Edad</th><th>Sexo</th><th>Contacto</th>
        <th>{vista === 'archivo' ? 'Desactivado' : 'Registrado'}</th><th></th>
      </tr>
    </thead>
    <tbody>
      {#each items as p (p.id)}
        <tr class:inactivo={vista === 'archivo'}>
          <td><strong>{p.nombres} {p.apellidos}</strong>{#if p.esNn} <span class="badge">NN</span>{/if}</td>
          <td class="mono">{p.cui ?? '—'}</td>
          <td>{p.fechaNacimiento ?? '—'}</td>
          <td>{edadDesdeNacimiento(p.fechaNacimiento)}</td>
          <td>{p.sexo ?? '—'}</td>
          <td>
            {#if p.telefono}<div>{p.telefono}</div>{/if}
            {#if p.email}<div class="dim">{p.email}</div>{/if}
          </td>
          <td>{fechaLista(p)}</td>
          <td>
            {#if vista === 'activos'}
              <button type="button" class="link" onclick={() => editar(p)}>Editar</button>
              <button type="button" class="link danger" onclick={() => eliminar(p)}>Desactivar</button>
            {:else}
              <button type="button" class="link" onclick={() => reactivar(p)}>Reactivar</button>
            {/if}
          </td>
        </tr>
      {:else}
        <tr><td colspan="8" class="empty">{loading ? 'Cargando…' : (vista === 'archivo' ? 'Sin pacientes en archivo para este mes.' : 'Sin pacientes.')}</td></tr>
      {/each}
    </tbody>
  </table>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  .toolbar { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; }
  .tabs { display: flex; gap: .35rem; }
  .tabs button { padding: .45rem .85rem; border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; color: #374151; }
  .tabs button.active { background: #dcfce7; border-color: #86efac; color: #166534; }
  .field.mes { display: flex; flex-direction: column; gap: .25rem; }
  .field.mes span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; }
  .field.mes input { padding: .45rem .55rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; }
  .search input { min-width: 240px; padding: 0.5rem 0.7rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; font-size: 0.9rem; }
  .hint { color: #6b7280; font-size: .88rem; margin: 0 0 .75rem; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin-bottom: 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .sub { margin: 0 0 1rem; font-size: 1rem; font-weight: 700; color: #166534; }
  .form-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.75rem; margin-bottom: 1rem; align-items: end; }
  .field { display: flex; flex-direction: column; gap: 0.3rem; }
  .field span { font-size: 0.72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.03em; }
  .field input, .field select { padding: 0.55rem 0.7rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; font-size: 0.88rem; }
  .chk { flex-direction: row; align-items: center; gap: 0.4rem; font-size: 0.85rem; color: #374151; }
  .btn { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.7rem 1.8rem; border: 0; border-radius: 10px; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; font: inherit; font-weight: 700; cursor: pointer; }
  .btn.ghost { background: #fff; color: #475569; border: 1px solid #e5e7eb; }
  .link { background: none; border: 0; color: #16a34a; font-weight: 600; cursor: pointer; padding: 0.2rem 0.4rem; font-size: 0.82rem; }
  .link.danger { color: #dc2626; }
  .tabla { width: 100%; border-collapse: collapse; }
  .tabla th { text-align: left; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.03em; color: #6b7280; padding: 0.6rem 0.8rem; background: #f0fdf4; }
  .tabla td { padding: 0.6rem 0.8rem; border-top: 1px solid #f3f4f6; font-size: 0.9rem; vertical-align: top; color: #111827; }
  tr.inactivo { opacity: .85; background: #fafafa; }
  .mono { font-family: ui-monospace, monospace; }
  .badge { background: #fee2e2; color: #b91c1c; padding: 0.1rem 0.5rem; border-radius: 999px; font-size: 0.7rem; margin-left: 0.3rem; }
  .dim { color: #6b7280; font-size: 0.8rem; }
  .err { color: #dc2626; }
  .empty { color: #9ca3af; text-align: center; padding: 1.5rem; }
  @media (max-width: 800px) { .form-grid { grid-template-columns: 1fr 1fr; } }
</style>
